import { createAction, createSlice } from '@reduxjs/toolkit';
import { getGridDefinition } from 'Components/WorksheetGrids/WorksheetGridDefinitions';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { isNil, omit } from 'ramda';
import * as oPath from '../../../Lib/Object/Path/Mutable';
import deepEquals from '../../../Lib/Object/deepEquals';
import { getSyncTargetDefaults } from '../../Core/legacyDataSync';
import { applySyncPatch, propertyRequiresSync } from '../DataSync';
import { IDataSyncState } from '../DataSync/types';
import {
  GetBalanceAction,
  GetBalanceResolveAction,
  RequestAction,
  RequestRejectAction,
  RequestResolveAction,
  SetBalanceAction,
  SyncErrorAction,
  SyncErrorRejectAction,
  SyncErrorResolveAction,
  WorksheetGridState,
} from './types';

const name = 'worksheetGrid';
const initialState: WorksheetGridState = {
  gridType: undefined,
  worksheetType: undefined,
  query: {
    params: {},
    defaultParams: {},
    requiredParams: [],
  },
  worksheets: {},
  commonData: {},
  requests: {},
  status: {
    refreshing: false,
    initialised: false,
    queryReady: false,
    syncError: false,
    syncingBalance: false,
  },
  additionalData: {},
};

const slice = createSlice({
  name,
  initialState,
  reducers: {
    init: (state, { payload: { gridType, worksheetType, query } = {} }) => {
      state.gridType = gridType;
      state.worksheetType = worksheetType;
      state.query = {
        ...query,
        params: query.defaultParams,
      };
      state.status.queryReady = !state.query.requiredParams.some(
        (p) => state.query.params[p] === undefined || state.query.params[p] === null
      );
      state.status.initialised = true;
      state.status.refreshing = false;
      state.worksheets = [];
      state.commonData = {};
      state.additionalData = {};
    },
    cleanup: (state) => {
      Object.assign(state, initialState);
    },
    setQueryValue: (state, { payload: { name, value } = {} }) => {
      if (value === undefined) oPath.del(state, `query.params.${name}`);
      else oPath.set(state, `query.params.${name}`, value, true);

      state.status.queryReady = !state.query.requiredParams.some(
        (p) => state.query.params[p] === undefined || state.query.params[p] === null
      );
    },
    refresh: (state) => {
      state.status.refreshing = true;
    },
    refreshResolve: (state, { payload: { worksheets } = {} }) => {
      const config = getGridDefinition(state.gridType).config;
      state.status.refreshing = false;
      state.worksheets = Object.fromEntries(
        worksheets.map((w: any) => [
          w.data.Id,
          {
            ...getSyncTargetDefaults(w),
            config,
            status: { readonly: { locked: false } },
          },
        ])
      );
    },
    refreshReject: (state) => {
      state.status.refreshing = false;
    },
    setWorksheetValue: (state, { payload: { worksheetId, name, value } = {} }) => {
      const previousValue = oPath.get(state, `worksheets.${worksheetId}.syncModel.${name}`);
      oPath.set(state, `worksheets.${worksheetId}.syncModel.${name}`, value, true);
      oPath.set(
        state,
        `worksheets.${worksheetId}.status.dirty`,
        propertyRequiresSync(oPath.get(state, `worksheets.${worksheetId}`) as IDataSyncState, name)
      );

      if (hasValue(value) && !deepEquals(previousValue, value)) {
        // Patch common data to worksheet on value change

        // Exclude common data that is restricted to changes on certain paths
        const commonDataSettings = getGridDefinition(state.gridType).commonDataSettings;
        const commonDataPatch = omit(
          Object.keys(commonDataSettings).filter(
            (k) =>
              hasNonEmptyValue(commonDataSettings[k].onlyApplyOnChangesTo) &&
              !commonDataSettings[k].onlyApplyOnChangesTo!.includes(name)
          ),
          state.commonData
        );
        if (hasNonEmptyValue(commonDataPatch)) {
          oPath.setMultiple(state, `worksheets.${worksheetId}.syncModel.data`, commonDataPatch, true);
        }
      }
    },
    setCommonDataValue: (state, { payload: { key, value } = {} }) => {
      if (isNil(value)) delete state.commonData[key];
      else state.commonData[key] = value;
    },
    setAdditionalDataValue: (state, { payload: { key, value } = {} }) => {
      if (isNil(value)) delete state.additionalData[key];
      else state.additionalData[key] = value;
    },
    synchroniseWorksheet: (state, { payload: { worksheetId } = {} }) => {
      state.worksheets[worksheetId].status.synchronising = true;
      state.worksheets[worksheetId].status.dirty = false;
    },
    synchroniseWorksheetComplete: (state, { payload: { worksheetId, syncPatch } = {} }) => {
      state.worksheets[worksheetId].status.synchronising = false;
      if (syncPatch) applySyncPatch(state.worksheets[worksheetId], syncPatch);
    },
    patchWorksheetStatus: (state, { payload: { worksheetStatusPatches } = {} }) => {
      worksheetStatusPatches.forEach((w: any) =>
        oPath.setMultiple(state, `worksheets.${w.worksheetId}.status`, w.status)
      );
    },
    /**
     * Posts a request to an endpoint other than the main get/post endpoints for the worksheet.
     * Should be used for test-specific endpoints that trigger further actions.
     * Worksheet will be synced after request is complete
     **/
    postRequest: (state, action: RequestAction) => {
      state.requests[action.payload.requestId] = { inProgress: true };
    },
    postRequestResolve: (state, action: RequestResolveAction) => {
      state.requests[action.payload.requestId] = { inProgress: false };
    },
    postRequestReject: (state, action: RequestRejectAction) => {
      state.requests[action.payload.requestId] = { inProgress: false };
    },
    postDefaultBalance: (state, action: SetBalanceAction) => {
      state.status.syncingBalance = true;
    },
    postDefaultBalanceReject: (state) => {
      state.status.syncingBalance = false;
    },
    postDefaultBalanceResolve: (state) => {
      state.status.syncingBalance = false;
    },
    getDefaultBalanceResolve: (state, action: GetBalanceResolveAction) => {
      state.additionalData['LaboratoryDefaultBalance'] = action.payload.balance;
    },
    getDefaultBalance: (state, action: GetBalanceAction) => {},
    getDefaultBalanceReject: (state) => {},

    syncError: (state, action: SyncErrorAction) => {
      const {
        payload: { syncPatch: { syncParams } = {} },
      } = action;

      if (syncParams) state.worksheets[action.payload.worksheetId].syncParams_synced = syncParams;
      state.worksheets[action.payload.worksheetId].status.syncError = true;
      state.status.syncError = true;
    },
    syncErrorResolve: (state, action: SyncErrorResolveAction) => {
      state.worksheets[action.payload.worksheetId].status.syncError = false;
      state.status.syncError = action.payload.retry ?? false;
    },
    syncErrorReject: (state, action: SyncErrorRejectAction) => {
      state.status.syncError = false;
      state.worksheets[action.payload.worksheetId].status.synchronising = false;
    },
  },
});

const { actions: sliceActions, reducer } = slice;
const actions = {
  ...sliceActions,
  updateWorksheetStatus: createAction(`${name}/updateWorksheetStatus`, ({ worksheetId }) => ({
    payload: { worksheetId },
  })),
  updateAllWorksheetStatus: createAction(`${name}/updateAllWorksheetStatus`),
};
export { actions, reducer };
